import { SortableTh, SortingDirection, Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { Switch } from '@components/core-ui/form-elements/Switch';
import Desktop from '@components/icons/Desktop.svg';
import DiagonalCross from '@components/icons/DiagonalCross.svg';
import Done from '@components/icons/Done.svg';
import DoubleDone from '@components/icons/DoubleDone.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { routes } from '@routes';
import { GetOrgMembersMemberResponseDto, PrismaOrganizationRoleDto } from 'app/API';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { InlineInheritIconButton } from '@components/core-ui/form-elements/buttons/InlineInheritIconButton';
import { EntityIconTableMuted30 } from '@components/core-ui/EntityIcon/variations/EntityIconTableMuted30';
import { CellContent } from './styles/CellContent';
import { CellNoEllipsisContent } from './styles/CellNoEllipsisContent';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import { FlexCellContent } from './styles/FlexCellContent';
import { StyledLink } from './styles/StyledLink';
import { SwitchWrapper } from './styles/SwitchWrapper';
import { TeamsCell } from './styles/TeamsCell';
import PencilIcon from '@components/icons/Pencil.svg';

type TProps = {
  members: GetOrgMembersMemberResponseDto[];
  isMutating: boolean;
  sortingField: string;
  sortingReverse: boolean;
  toggleSort: (field: string) => void;
  onToggleEnabled: (member: GetOrgMembersMemberResponseDto) => void;
  onToggleCanManageLayouts: (member: GetOrgMembersMemberResponseDto) => void;
  onRemoveTeam: (team: PrismaOrganizationRoleDto, userId: string) => void;
  reload: () => void;
};

export const UsersTable = ({
  members,
  isMutating,
  sortingField,
  sortingReverse,
  toggleSort,
  onToggleEnabled,
  onToggleCanManageLayouts,
  onRemoveTeam,
  reload,
}: TProps) => {
  const { me, organization } = useAuthContext();
  const { openModal, closeModal } = useModalContext();
  const userHasManageMembersPermission = organization?.permissions?.manageMembers;
  const canManageOrganization = !!organization?.permissions?.manageOrganization;

  const onDeleteMemberClick = (userId: string) => {
    openModal(ModalNamesEnum.DeleteMemberModal, {
      userId,
      organizationId: organization?.id,
      onSuccess: () => {
        reload();
        closeModal();
      },
    });
  };

  const editConcurrentLimit = (user: GetOrgMembersMemberResponseDto) => {
    openModal(ModalNamesEnum.EditConcurrentJobsLimit, {
      onSuccess: () => {
        reload();
        closeModal();
      },
      uid: user?.id,
      concurrentJobsLimit: user?.concurrentJobsLimit ?? 0,
    });
  };

  return (
    <TableWrapper>
      <Table>
        <Thead>
          <Tr>
            <Th style={{ width: '1%' }}></Th>
            <SortableTh style={{ width: '20%' }} onClick={() => toggleSort('name')}>
              Name
              {sortingField === 'name' && <SortingDirection reverse={sortingReverse} />}
            </SortableTh>
            <Th style={{ width: '50%' }}>Contacts</Th>
            <Th style={{ width: '1%' }}>Enabled</Th>
            <Th style={{ width: '50%' }}>Teams</Th>
            <SortableTh style={{ width: '1%' }} onClick={() => toggleSort('createdAt')}>
              Created
              {sortingField === 'createdAt' && <SortingDirection reverse={sortingReverse} />}
            </SortableTh>
            <Th style={{ width: '1%' }}>CLAIMED JOB LIMIT</Th>
            <Th style={{ width: '1%' }} title="Whether this member can create, replace, and delete cloud layouts">
              MANAGE CLOUD LAYOUTS
            </Th>
            <Th style={{ width: '3%', paddingTop: '6px' }} title="Downloaded & Signed in with Desktop App">
              <Desktop width="16px" />
            </Th>
            <Th style={{ width: '1%', paddingTop: '6px' }} title=""></Th>
          </Tr>
        </Thead>
        <Tbody>
          {members.map((data) => {
            const isMe = me.id === data.id;
            const teams = (data.teams ?? []).filter((team) => team.organizationId === organization?.id);

            return (
              <Tr key={data.id}>
                <Td>
                  <FlexCellContent>
                    <EntityIconTableMuted30 name={`${data.name} ${data.lastname}`} src={data.avatar ?? undefined} />
                  </FlexCellContent>
                </Td>
                <Td>
                  <FlexCellContent>
                    <StyledLink to={routes.userDetails.make(organization?.slug ?? '', data.id)}>
                      {data.name} {data.lastname}
                    </StyledLink>
                  </FlexCellContent>
                </Td>
                <Td>
                  <FlexCellContent>
                    <CellContent>
                      <span style={{ display: 'block' }}>{data.email}</span>
                      <span style={{ display: 'block' }}>{data.phone}</span>
                    </CellContent>
                  </FlexCellContent>
                </Td>
                <Td>
                  <FlexCellContent>
                    <SwitchWrapper>
                      <Switch
                        checked={!data.disabledInOrg}
                        onChange={() => onToggleEnabled(data)}
                        disabled={isMe || isMutating}
                      />
                    </SwitchWrapper>
                  </FlexCellContent>
                </Td>
                <Td>
                  <FlexCellContent>
                    <CellNoEllipsisContent>
                      {teams.map((item) => (
                        <TeamsCell key={item.name} color={item.colour}>
                          {item.name}
                          {teams.length > 1 ? (
                            <InlineInheritIconButton
                              title="Remove from team"
                              aria-label="Remove from team"
                              onClick={() => onRemoveTeam(item, data.id)}
                            >
                              <DiagonalCross />
                            </InlineInheritIconButton>
                          ) : null}
                        </TeamsCell>
                      ))}
                    </CellNoEllipsisContent>
                  </FlexCellContent>
                </Td>
                <Td>
                  <FlexCellContent>{getAmericanTime(data.createdAt)}</FlexCellContent>
                </Td>
                <Td>
                  <FlexCellContent className="flex justify-center gap-2">
                    {data.concurrentJobsLimit ? data.concurrentJobsLimit : 'Unlimited'}
                    {userHasManageMembersPermission ? (
                      <PencilIcon
                        className="pencil text-[var(--color-text-muted)] hover:text-[var(--color-accent)] cursor-pointer"
                        onClick={() => editConcurrentLimit(data)}
                      />
                    ) : null}
                  </FlexCellContent>
                </Td>
                <Td>
                  <FlexCellContent className="flex justify-center">
                    {canManageOrganization ? (
                      <SwitchWrapper>
                        <Switch
                          checked={!!data.canManageLayouts}
                          onChange={() => onToggleCanManageLayouts(data)}
                          disabled={isMutating}
                          testId={`can-manage-layouts-${data.id}`}
                        />
                      </SwitchWrapper>
                    ) : data.canManageLayouts ? (
                      <Done width="16px" />
                    ) : null}
                  </FlexCellContent>
                </Td>
                <Td>
                  <FlexCellContent>
                    {data.signedInWithDesktop ? (
                      <DoubleDone width="24px" />
                    ) : data.downloadedDesktop ? (
                      <Done width="16px" />
                    ) : (
                      ''
                    )}
                  </FlexCellContent>
                </Td>
                <Td>
                  <FlexCellContent>
                    {organization?.permissions?.manageMembers && !isMe ? (
                      <IconButtonDelete
                        title="Delete this user from the Organization"
                        onClick={() => onDeleteMemberClick(data.id)}
                      >
                        <TrashBin />
                      </IconButtonDelete>
                    ) : null}
                  </FlexCellContent>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableWrapper>
  );
};
